import React, { useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSiteUrl } from '@utils/config';
import FaqCategoryMenu from '../FaqCategoryMenu/FaqCategoryMenu';
import FaqCategoryBlock from '@components/Shared/FAQ/FaqCategoryBlock';
import Breadcrumb from '../../Shared/Breadcrumb/Breadcrumb';
import { FaqCategoryContainerStyles } from './FaqCategoryContainer.styled';
import HeroSection from '@components/Shared/HeroSection/HeroSection';

interface FaqCategoryContainerProps {
  section?: {
    sectionHeader?: string;
    sectionIntro?: string;
    categoryPageLink?: {
      link?: string;
    };
    faqs?: Array<{
      active: boolean;
      slug: string;
      faqQuestion: string;
      faqAnswer?: string;
      faqBackgroundColor?: string;
      faqAnswerVideo?: Array<{
        embeddedContent: string;
      }>;
      answerLinks?: Array<{
        displayLinkAs: string;
        link: string;
        linkText: string;
      }>;
      disclaimer?: string;
    }>;
  };
  heroSection?: {
    heroBackgroundColor?: string;
    heroContent?: {
      sectionContent?: string;
      sectionHeader?: string;
    };
  };
  faqData?: {
    faqQuestion?: string;
    metaDescription?: string;
    titleTag?: string;
  };
  metaData?: {
    metaDescription?: string;
    ogImage?: {
      url?: string;
    };
    titleTag?: string;
  };
}

type FaqInfo = {
  faqQuestion?: string;
  metaDescription?: string;
  titleTag?: string;
  active?: boolean;
  slug?: string;
  faqAnswer?: string;
  faqBackgroundColor?: string;
  faqAnswerVideo?: Array<{
    embeddedContent: string;
  }>;
  answerLinks?: Array<{
    displayLinkAs: string;
    link: string;
    linkText: string;
  }>;
  disclaimer?: string;
} | null;

const FaqCategoryContainer = ({
  section,
  heroSection,
  faqData,
  metaData,
}: FaqCategoryContainerProps) => {
  const [faqInfo, setFaqInfo] = useState<FaqInfo>(faqData ?? null);
  const router = useRouter();

  return (
    <>
      <Head>
        <title>{faqInfo?.titleTag || metaData?.titleTag}</title>
        <meta
          name="description"
          content={faqInfo?.metaDescription || metaData?.metaDescription}
        />
        <link rel="canonical" href={`${getSiteUrl(router.pathname)}`} />
        <meta
          property="og:image"
          content={
            metaData?.ogImage?.url ||
            getSiteUrl('/public-assets/images/clayton-logo.jpg')
          }
        />
      </Head>
      <HeroSection heroSection={heroSection} />
      <FaqCategoryContainerStyles>
        <Breadcrumb
          navItems={[
            { text: 'FAQs', url: '/faq' },
            { text: section?.sectionHeader },
          ]}
        />
        <div className="faq-category">
          <FaqCategoryMenu />
          <section className="w-full lg:w-2/3 xl:w-3/4">
            <FaqCategoryBlock
              section={
                section ?? { sectionHeader: '', sectionIntro: '', faqs: [] }
              }
              setFaqInfo={setFaqInfo}
              canScroll={true}
            />
          </section>
        </div>
      </FaqCategoryContainerStyles>
    </>
  );
};

export default FaqCategoryContainer;
